import type { Locale } from '../i18n';
import type { PageContent } from '../types/theme';

export const localizedPageContent: Record<Locale, PageContent> = {
  zh: {
    nav: {
      logo: 'NeuroAI',
      menuItems: ['功能', '演示', '价格', '文档', '博客'],
      ctaText: '开始使用',
    },
    hero: {
      badge: 'AI 开发助手',
      title: '用智能代码助手更快交付',
      subtitle: 'NeuroAI 理解你的代码库，预测下一步操作，并帮助团队用更短时间完成产品功能。',
      ctaPrimary: '免费试用',
      ctaSecondary: '查看演示',
    },
    features: {
      badge: '能力',
      title: '构建产品所需的一切',
      items: [
        {
          icon: 'zap',
          title: '快速响应',
          description: '毫秒级给出上下文建议，提前加载你可能需要的代码路径。',
        },
        {
          icon: 'brain',
          title: '理解代码库',
          description: '识别引用、用法和模块关系，帮助你更快读懂复杂项目。',
        },
        {
          icon: 'shield',
          title: '安全优先',
          description: '面向企业的安全策略，支持私有环境和审计要求。',
        },
        {
          icon: 'layers',
          title: '多语言支持',
          description: '覆盖主流编程语言，并融入现有开发流程。',
        },
        {
          icon: 'git-branch',
          title: 'Git 集成',
          description: '辅助代码评审、提交说明和分支管理，让协作更顺畅。',
        },
        {
          icon: 'users',
          title: '团队协作',
          description: '共享代码片段、团队规范和可复用模式。',
        },
      ],
    },
    demo: {
      badge: '实时演示',
      title: '看看 NeuroAI 如何工作',
      description: '体验智能补全、自然语言查询和自动化测试建议。',
      mockupType: 'chat',
      prompt: '如何在 React 应用里实现认证？',
      response: '建议使用 JWT 和刷新令牌。下面是一套包含安全边界的实现方案...',
      completion: '正在生成 Express API、参数校验和测试用例...',
    },
    pricing: {
      badge: '简单价格',
      title: '今天开始构建',
      popularLabel: '最受欢迎',
      plans: [
        {
          name: 'Starter',
          price: '$0',
          period: '永久',
          description: '适合刚开始使用的个人开发者。',
          features: ['每天 100 次代码补全', '基础代码搜索', '单文件编辑', '社区支持'],
          cta: '开始使用',
        },
        {
          name: 'Pro',
          price: '$19',
          period: '每月',
          description: '适合需要完整能力的专业开发者。',
          features: ['不限次数补全', '完整代码库索引', '多文件重构', '优先支持', '自定义片段'],
          cta: '免费试用',
          popular: true,
        },
        {
          name: 'Team',
          price: '$49',
          period: '每用户/月',
          description: '适合一起交付产品的团队。',
          features: ['包含 Pro 全部能力', '团队代码库', '管理员面板', 'SSO 与 SAML', '专属支持', '自定义集成'],
          cta: '联系销售',
        },
      ],
    },
    footer: {
      brand: 'NeuroAI',
      tagline: '面向现代开发团队的智能代码助手。',
      copyright: '保留所有权利。',
      links: [
        {
          title: '产品',
          items: ['功能', '价格', '更新日志', '路线图'],
        },
        {
          title: '资源',
          items: ['文档', 'API 参考', '博客', '社区'],
        },
        {
          title: '公司',
          items: ['关于', '招聘', '联系', '媒体包'],
        },
        {
          title: '法律',
          items: ['隐私', '条款', '安全', 'Cookie'],
        },
      ],
    },
  },
  en: {
    nav: {
      logo: 'NeuroAI',
      menuItems: ['Features', 'Demo', 'Pricing', 'Docs', 'Blog'],
      ctaText: 'Get Started',
    },
    hero: {
      badge: 'AI-Powered Development',
      title: 'Build faster with intelligent code assistance',
      subtitle: 'NeuroAI understands your codebase, predicts your next move, and helps you ship features in half the time.',
      ctaPrimary: 'Start Free Trial',
      ctaSecondary: 'View Demo',
    },
    features: {
      badge: 'Capabilities',
      title: 'Everything you need to build',
      items: [
        {
          icon: 'zap',
          title: 'Lightning Fast',
          description: 'Get context-aware suggestions in milliseconds. The model learns your patterns and preloads what you need.',
        },
        {
          icon: 'brain',
          title: 'Code Understanding',
          description: 'Navigate references, find usages, and understand relationships across your entire codebase.',
        },
        {
          icon: 'shield',
          title: 'Security First',
          description: 'Enterprise-grade controls for private infrastructure, compliance, and audit requirements.',
        },
        {
          icon: 'layers',
          title: 'Multi-language',
          description: 'Support for major programming languages that fits into your existing workflow.',
        },
        {
          icon: 'git-branch',
          title: 'Git Integration',
          description: 'Smarter PR reviews, commit messages, and branch management for collaborative teams.',
        },
        {
          icon: 'users',
          title: 'Team Collaboration',
          description: 'Share snippets, create team libraries, and enforce coding standards across the organization.',
        },
      ],
    },
    demo: {
      badge: 'See It In Action',
      title: 'Watch how NeuroAI works',
      description: 'Experience intelligent code completion, natural language queries, and automated testing suggestions.',
      mockupType: 'chat',
      prompt: 'How do I implement authentication in my React app?',
      response: "I recommend using JWT with refresh tokens. Here's a complete implementation with security best practices...",
      completion: 'Generating REST API with Express, validation, and testing...',
    },
    pricing: {
      badge: 'Simple Pricing',
      title: 'Start building today',
      popularLabel: 'Most Popular',
      plans: [
        {
          name: 'Starter',
          price: '$0',
          period: 'forever',
          description: 'Perfect for individual developers getting started.',
          features: ['100 code completions/day', 'Basic code search', 'Single file editing', 'Community support'],
          cta: 'Get Started',
        },
        {
          name: 'Pro',
          price: '$19',
          period: 'per month',
          description: 'For professional developers who need more.',
          features: ['Unlimited completions', 'Full codebase indexing', 'Multi-file refactoring', 'Priority support', 'Custom snippets'],
          cta: 'Start Free Trial',
          popular: true,
        },
        {
          name: 'Team',
          price: '$49',
          period: 'per user/month',
          description: 'For teams shipping production code together.',
          features: ['Everything in Pro', 'Team code libraries', 'Admin dashboard', 'SSO & SAML', 'Dedicated support', 'Custom integrations'],
          cta: 'Contact Sales',
        },
      ],
    },
    footer: {
      brand: 'NeuroAI',
      tagline: 'Intelligent code assistance for modern developers.',
      copyright: 'All rights reserved.',
      links: [
        {
          title: 'Product',
          items: ['Features', 'Pricing', 'Changelog', 'Roadmap'],
        },
        {
          title: 'Resources',
          items: ['Documentation', 'API Reference', 'Blog', 'Community'],
        },
        {
          title: 'Company',
          items: ['About', 'Careers', 'Contact', 'Press Kit'],
        },
        {
          title: 'Legal',
          items: ['Privacy', 'Terms', 'Security', 'Cookies'],
        },
      ],
    },
  },
};
